/**
 * The answers every studio page shares (client launch, Nov 2026): a studio switched off for the team (403
 * `feature_closed`), every client paused by Beatly (403 `clients_paused`), the client AI terms not accepted yet (403
 * `terms_required`, when the API enforces them) and a monthly cap reached (429 `cap_reached`).
 *
 * Pages keep their own error handling; this module only notices those three answers wherever they come from (the
 * cowork API helpers, raw fetches to the cowork API, the audio service) and tells the app shell, which shows one calm
 * message for them (cowork/launch/LaunchSignals.tsx). Nothing here changes a response.
 */

export type CapStudio = 'deck' | 'voice' | 'audio' | 'video' | 'brand';

/** One monthly cap as GET /v1/me (`caps`) and the 429 body describe it (cowork engine/usageCaps.ts). */
export interface CapStatus { studio: CapStudio | string; unit: string; limit: number; used: number; resetsAt: string }

/** The 429 body (cowork capErrorBody). */
export interface CapReachedBody { error: 'cap_reached'; studio: string; unit: string; limit: number; used: number; resetsAt: string; message?: string }

export type ApiSignal =
  | { kind: 'feature_closed'; feature: string | null }
  | { kind: 'clients_paused' }
  /** 403 `terms_required`: the API refused a client who has not accepted the client AI terms (reopens the modal). */
  | { kind: 'terms_required' }
  | { kind: 'cap_reached'; cap: CapReachedBody };

function str(x: unknown): string | null {
  return typeof x === 'string' && x ? x : null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** The answer's JSON body, unwrapping FastAPI's `{detail: {...}}` (the audio service). */
function answerBody(body: unknown): Record<string, unknown> {
  const b = asRecord(body) ?? {};
  return asRecord(b.detail) ?? b;
}

/**
 * Which shared answer is this, if any? The contract's `{error:'feature_closed'}` and the staff gate's older
 * `{error:'<sentence>', code:'feature_closed'}` are both understood, at the top level or under FastAPI's `detail`.
 */
export function classifyApiError(status: number, body: unknown): ApiSignal | null {
  const b = answerBody(body);
  const tag = str(b.error) ?? '';
  const code = str(b.code) ?? '';
  if (status === 403) {
    if (tag === 'clients_paused' || code === 'clients_paused') return { kind: 'clients_paused' };
    if (tag === 'feature_closed' || code === 'feature_closed') return { kind: 'feature_closed', feature: str(b.feature) };
    if (tag === 'terms_required' || code === 'terms_required') return { kind: 'terms_required' };
    return null;
  }
  if (status === 429 && (tag === 'cap_reached' || code === 'cap_reached')) {
    return {
      kind: 'cap_reached',
      cap: {
        error: 'cap_reached',
        studio: str(b.studio) ?? '',
        unit: str(b.unit) ?? '',
        limit: typeof b.limit === 'number' ? b.limit : 0,
        used: typeof b.used === 'number' ? b.used : 0,
        resetsAt: str(b.resetsAt) ?? '',
        message: str(b.message) ?? undefined,
      },
    };
  }
  return null;
}

function statusOf(err: unknown): number | null {
  const e = asRecord(err);
  return e && typeof e.status === 'number' ? e.status : null;
}

/**
 * The cap a thrown error says was reached (a CoworkApiError or AudioStudioApiError with a 429 cap body), else null.
 * An older helper that kept only the `error` string ("cap_reached") still counts, with no numbers. Any other 429 (an
 * hourly AI limit, a busy render slot, the licence guard) is not a monthly cap.
 */
export function capReachedOf(err: unknown): CapReachedBody | null {
  const e = asRecord(err);
  if (e && e.body !== undefined) {
    const status = statusOf(err);
    const s = classifyApiError(429, e.body);
    if (s?.kind === 'cap_reached' && (status === null || status === 429)) return s.cap;
  }
  if (err instanceof Error && err.message === 'cap_reached') {
    return { error: 'cap_reached', studio: '', unit: '', limit: 0, used: 0, resetsAt: '' };
  }
  return null;
}

/** Is this thrown error a cap answer? Pages use it to leave the message to the shared cap toast. */
export function isCapReachedError(err: unknown): boolean {
  return capReachedOf(err) !== null;
}

export type AccessClosed = 'feature_closed' | 'clients_paused';

/** A studio switched off for the team (403 feature_closed) or every client paused (403 clients_paused), else null. */
export function accessClosedOf(err: unknown): AccessClosed | null {
  const e = asRecord(err);
  if (e && e.body !== undefined) {
    const status = statusOf(err);
    const s = classifyApiError(403, e.body);
    if ((s?.kind === 'feature_closed' || s?.kind === 'clients_paused') && (status === null || status === 403)) return s.kind;
  }
  if (err instanceof Error && (err.message === 'feature_closed' || err.message === 'clients_paused')) return err.message;
  return null;
}

/** Is this thrown error a closed, paused or terms answer? */
export function isStudioClosedError(err: unknown): boolean {
  const e = asRecord(err);
  return !!e && statusOf(err) === 403 && classifyApiError(403, e.body) !== null;
}

type Listener = (s: ApiSignal) => void;
const listeners = new Set<Listener>();

export function onApiSignal(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function emitApiSignal(s: ApiSignal): void {
  listeners.forEach((cb) => { try { cb(s); } catch { /* a listener never breaks another */ } });
}

/** Note one answer: emits a signal when it is one of the three shared answers. */
export function noteApiAnswer(status: number, body: unknown): void {
  const s = classifyApiError(status, body);
  if (s) emitApiSignal(s);
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url ?? '';
}

const INSTALLED = Symbol.for('beatly.apiSignals.installed');

/**
 * Watch every fetch to the given API bases (the cowork API, the audio service) for the three shared answers. The
 * response the caller gets is untouched (a clone is read). Idempotent: installing twice wraps once.
 */
export function installApiSignalObserver(bases: readonly string[]): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const w = window as unknown as Record<symbol, unknown> & { fetch: typeof fetch };
  if (w[INSTALLED]) return;
  const roots = bases.map((b) => b.replace(/\/+$/, '')).filter(Boolean);
  if (!roots.length) return;
  const original = w.fetch.bind(window);
  const wrapped: typeof fetch = async (input, init) => {
    const res = await original(input, init);
    try {
      if ((res.status === 403 || res.status === 429) && roots.some((r) => urlOf(input).startsWith(r))) {
        res.clone().json().then((body) => noteApiAnswer(res.status, body)).catch(() => { /* not JSON */ });
      }
    } catch { /* never let the observer break a request */ }
    return res;
  };
  w.fetch = wrapped;
  w[INSTALLED] = true;
}
